import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject, input, signal } from '@angular/core';
import { ActivityEntityName, ContactEntity, ContactType, ENTITY_QUEUE_TRANSFER_OWNERSHIP_COMMAND, EntityQueueDefinition } from '@jms/model';
import { EntityDataService } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { EntityPickerDirective, SelectComponent } from '@workbuddy/ui/v2/fields/select';

/**
 * Picks the employee who becomes the queue's owner. A separate modal because a manifest declares only
 * create / edit / preview, and ownership is not a field the queue wizard should expose.
 */
@Component({
  selector: 'wb-queue-transfer-ownership',
  standalone: true,
  imports: [SelectComponent, EntityPickerDirective],
  template: `
    <div class="fieldset">
      <legend>Transfer Ownership</legend>
      <div class="message">Choose the employee who will own "{{ $queue().name }}". They can edit, share and delete it from their inbox.</div>
      <div class="field">
        <label>New owner</label>
        <div class="value">
          <wb-select [entityPicker]="ActivityEntityName.ContactEmployee" [entityOptions]="employeeOptions" [(value)]="$owner" [clearable]="false" appendTo="body" placeholder="Select an employee" />
        </div>
      </div>
    </div>
    <div class="actions">
      <button type="button" class="btn btn-none" (click)="cancel()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="transfer()" [disabled]="!$owner() || $saving()">Transfer</button>
    </div>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'wb-queue-transfer-ownership' },
})
export class QueueTransferOwnershipDialogComponent {

  public readonly $queue = input.required<EntityQueueDefinition>({ alias: 'queue' });

  protected readonly ActivityEntityName = ActivityEntityName;
  protected readonly employeeOptions = { types: [ContactType.Employee] };
  protected readonly $owner = signal<ContactEntity | null>(null);
  protected readonly $saving = signal(false);

  private readonly dialogRef = injectDialogRef<boolean>();
  private readonly data = inject(EntityDataService);
  private readonly toasts = inject(ToastService);

  protected cancel(): void {

    this.dialogRef.close(false);
  }

  protected async transfer(): Promise<void> {

    const owner = this.$owner();
    const queue = this.$queue();
    if (!owner?._id || !queue?._id) return;

    this.$saving.set(true);
    try {
      await this.data.executeCommand(ActivityEntityName.EntityQueueDefinition, ENTITY_QUEUE_TRANSFER_OWNERSHIP_COMMAND, { queueId: queue._id, toUserId: owner._id });
      this.data.invalidateItems(ActivityEntityName.EntityQueueDefinition);
      this.toasts.success('Queues', `"${queue.name}" now belongs to ${owner.name}`);
      this.dialogRef.close(true);
    } catch (error) {
      this.toasts.error('Ownership not transferred', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      this.$saving.set(false);
    }
  }
}
